/*
 * The application shell (piece 12): the fixed header, the desktop navigation
 * bar and its collapsed rail, the phone drawer, and the Page Module's banner,
 * content and footer. Every dimension the specification fixes reads a token.
 */

/*
 * 🔨 The frame is the viewport, and the Content region is the only thing in it
 * that scrolls (piece 12). The header, the navigation bar, the Title Banner and
 * the Footer hold their place while the content moves under them.
 * 💰 The frame used to be `min-height: 100vh`, so a long page grew the document
 * and every one of those four scrolled away with it.
 */
.frame {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--surface-2);
}

/* The navigation bar and the page share what the header and any banner leave. */
.body {
  flex: 1 1 auto;
  display: flex;
  min-height: 0;
  min-width: 0;
}

/* ---- Global Header ---- */

/*
 * The header renders above every other module, including a modal's backdrop,
 * and below the modal itself, and it never scrolls. The plan states no z-index
 * anywhere, so this stacking is written here on purpose: left unstated, the
 * framework's own order wins. A <dialog> in the top layer is above all of it.
 */
.header {
  position: relative;
  flex: none;
  height: var(--header-height);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0 var(--space-3);
  background: var(--header-bg);
  color: var(--on-status-fill);
  z-index: 100;
}

/* The wordmark: name over a rule, with the suffix tracked wide beneath it. */
.logo {
  flex: none;
  /* Under md the search field could not hold its own placeholder beside the logo, so the logo goes first (product owner, 2026-09-10). */
  display: none;
  flex-direction: column;
  align-items: stretch;
  padding: var(--logo-extra-tap);
  margin: calc(-1 * var(--logo-extra-tap));
  border-radius: var(--radius-medium);
  color: inherit;
  line-height: 1;
  text-decoration: none;
}

/* The wordmark is the supplied file, drawn at the header's own height so its two lines keep the mark's proportion. */
.wordmark {
  display: block;
  height: var(--wordmark-height);
}

.wordmarkImage {
  display: block;
  height: var(--wordmark-height);
  width: auto;
}

/* The avatar badge: initials in a circle, the name beside it (piece 12). */
.avatarBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--avatar-badge-size);
  height: var(--avatar-badge-size);
  border-radius: var(--radius-stadium);
  background: var(--navy-primary);
  color: var(--on-status-fill);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-bold);
  position: relative;
}

/* The dot that says the change log holds entries this browser has not been shown (decision 0636): on the badge's corner, and after the menu item's words. */
.whatsNewDot {
  flex: none;
  width: var(--list-row-status-dot-size);
  height: var(--list-row-status-dot-size);
  border-radius: var(--radius-stadium);
  background: var(--gold-accent);
}

.avatarBadge .whatsNewDot {
  position: absolute;
  top: 0;
  right: 0;
}

/* 🔒 The dot on the avatar menu's Account Settings row while a passkey is offered (decision 1442; T693): the same mark the change log uses, after the row's words. */
.securityDot {
  flex: none;
  width: var(--list-row-status-dot-size);
  height: var(--list-row-status-dot-size);
  border-radius: var(--radius-stadium);
  background: var(--gold-accent);
  margin-left: var(--space-2);
}

.logo:focus-visible,
.headerButton:focus-visible {
  outline: var(--focus-ring);
  outline-offset: 2px;
}

/*
 * Global search: one wide rounded field on the header's own dark ground (piece
 * 12). 💰 `searchInput` named a class the stylesheet never defined, so the
 * field fell back to the browser's default box — white, square and the wrong
 * width (found while building, 2026-09-09).
 */
.headerSearch {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.searchInput {
  width: 100%;
  max-width: var(--header-search-width);
  height: var(--button-height-medium);
  padding: 0 var(--space-4);
  border: 0;
  border-radius: var(--radius-stadium);
  background: var(--header-field-bg);
  color: var(--on-status-fill);
  font: inherit;
  font-size: var(--font-size-small);
}

.searchInput::placeholder {
  color: var(--on-status-fill);
  opacity: 0.66;
}

.searchInput:focus-visible {
  outline: 2px solid var(--on-status-fill);
  outline-offset: var(--focus-ring-offset);
}

.headerSearch {
  position: relative;
  flex: 1 1 auto;
  max-width: 520px;
}

/*
 * 🔒 Decision 2770 (T1616): the module chooser at the search field's left, a stadium on the same dark ground as
 * the field, reading the module the search asks. Its menu hangs under it at the width of its own rows.
 */
.headerSearch {
  gap: var(--space-1);
}

.searchModuleWrap {
  position: relative;
  flex: none;
}

.searchModule {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  height: var(--button-height-medium);
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-stadium);
  background: var(--header-field-bg);
  color: var(--on-status-fill);
  font: inherit;
  font-size: var(--font-size-small);
  white-space: nowrap;
  cursor: pointer;
}

.searchModule:hover {
  background: var(--header-hover);
}

.searchModule:focus-visible {
  outline: 2px solid var(--on-status-fill);
  outline-offset: var(--focus-ring-offset);
}

/* On a phone the chosen module's name is clipped short, so the field keeps room to be typed in. */
.searchModuleName {
  max-width: 9ch;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (--bp-md) {
  .searchModuleName {
    max-width: none;
  }
}

.searchModuleMenu {
  inset: calc(100% + var(--space-1)) auto auto 0;
  width: max-content;
  min-width: var(--avatar-menu-min-width);
  overflow-y: auto;
}

.searchModuleMenu .searchRow[aria-checked='true'] {
  background: var(--selection-bg);
  font-weight: var(--font-weight-semibold);
}

@media (--bp-md) {
  .logo {
    display: inline-flex;
  }
}

.headerButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--tap-target-min);
  min-width: var(--tap-target-min);
  padding: 0 var(--space-2);
  border: 0;
  border-radius: var(--radius-medium);
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.headerButton:hover {
  background: var(--header-hover);
}

/*
 * The drawer's own toggle exists below md alone, where the desktop bar is
 * gone. 🔨 This rule sits after `.headerButton` on purpose: the two share a
 * specificity, and the later one wins. 💰 It sat before, so the button's own
 * `display` beat the media rule and the toggle stood beside the desktop bar
 * on every screen — a phone drawer and a sidebar at once (found while
 * building, 2026-09-09).
 */
.drawerToggle {
  display: inline-flex;
}

@media (--bp-md) {
  .drawerToggle {
    display: none;
  }
}

/* The name stands beside the badge where there is room; on a phone the badge stands alone and the search keeps its width. */
.avatarName {
  display: none;
  max-width: 12ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (--bp-md) {
  .avatarName {
    display: inline;
  }
}

.spacer {
  flex: 1 1 auto;
}

/* The results panel: a fixed maximum height with a visible scrollbar, never
   unbounded growth down the page and never a hover-to-reveal native bar. */
.searchPanel {
  position: absolute;
  inset: calc(100% + var(--space-1)) 0 auto 0;
  max-height: var(--search-panel-max-height);
  overflow-y: scroll;
  padding: var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  color: var(--text);
  box-shadow: var(--panel-shadow);
}

/* The avatar menu hangs under its bubble at the width of its own rows, never the header's (product owner, 2026-09-11). */
.avatarWrap {
  position: relative;
}

.avatarMenu {
  inset: calc(100% + var(--space-1)) 0 auto auto;
  width: max-content;
  min-width: var(--avatar-menu-min-width);
  /* 🔒 T353: right-aligned to the avatar and never past the screen's left edge; a
     row wider than that wraps inside the menu. 💰 The test build's developer index
     row took the menu off the left of a phone, cutting What's new to its dot. */
  max-width: var(--avatar-menu-max-width);
  overflow-y: auto;
}

.avatarMenu .buildStamp {
  white-space: normal;
  overflow-wrap: anywhere;
}

.searchGroup + .searchGroup {
  margin-top: var(--space-3);
}

.searchGroupHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: var(--section-label-size);
  color: var(--section-label-color);
  font-weight: var(--section-label-weight);
  text-transform: uppercase;
  letter-spacing: var(--section-label-tracking);
}

.searchRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-1) var(--space-2);
  border: 0;
  border-radius: var(--radius-medium);
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.searchRow[aria-selected='true'],
.searchRow:hover {
  background: var(--selection-bg);
}

/* The version line at the foot of the avatar menu (decision 0358): it reads,
   it is not a row that can be pressed, and it is set apart by a rule above. */
.buildStamp {
  margin: var(--space-1) 0 0;
  padding: var(--space-1) var(--space-2) 0;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  white-space: nowrap;
}

.searchEmpty {
  padding: var(--space-3);
  color: var(--text-muted);
}

/* The test-environment banner, and the emulation banner beneath the header. */
/* In the bar from md up; below md the same text stands as its own strip beneath the bar. */
.testBanner {
  display: none;
  align-items: center;
  flex: none;
  gap: var(--space-2);
  padding: 0 var(--space-2);
  border-radius: var(--radius-medium);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
  font-size: var(--font-size-caption);
  white-space: nowrap;
}

.testStrip {
  flex: none;
  padding: var(--space-1) var(--space-3);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
  font-size: var(--font-size-caption);
  text-align: center;
}

@media (--bp-md) {
  .testBanner {
    display: inline-flex;
  }

  .testStrip {
    display: none;
  }
}

.emulationBanner {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  background: var(--status-warning-bg);
  color: var(--status-warning-text);
  font-weight: 600;
}

.offlineBanner {
  flex: none;
  padding: var(--space-2) var(--space-3);
  background: var(--status-inactive-bg);
  color: var(--status-inactive-text);
}

/* ---- Global Navigation Bar ---- */

/* 🔨 The bar scrolls its own items when they outrun the viewport, and the
   collapse control stays pinned at its foot by `.navFooter`'s auto margin. */
.nav {
  flex: none;
  display: flex;
  flex-direction: column;
  width: var(--nav-expanded-width);
  min-height: 0;
  padding-top: var(--space-2);
  border-right: 1px solid var(--border);
  background: var(--surface);
  overflow-y: auto;
}

.nav[data-rendering='rail'] {
  width: var(--nav-rail-width);
}

/* 🔨 Every row keeps the one row height, pinned or grouped (piece 12; T406):
   Dashboard and Calendar are the bar's own flex items, and without `flex: none`
   they shrank to their text whenever the rows outran the viewport. */
.navRow {
  display: flex;
  align-items: center;
  flex: none;
  gap: var(--space-2);
  height: var(--nav-row-height);
  padding: 0 var(--space-3);
  border-left: 3px solid transparent;
  color: var(--text);
  font-size: var(--font-size-body);
  text-decoration: none;
  white-space: nowrap;
}

.navRow:hover {
  background: var(--surface-2);
}

/* 🔨 The active item is an accent left bar and a bold label (piece 12;
   decision 0320). 💰 It was built gold, then navy, before the accent was red. */
.navRow[aria-current='page'] {
  border-left-color: var(--accent);
  background: var(--surface-2);
  font-weight: 700;
}

.nav[data-rendering='rail'] .navRow {
  justify-content: center;
  padding: 0;
}

/* Decision 3072: a menu entry's count balloon stands at the row's end, or on the icon's corner in the rail. */
.navBadge {
  display: inline-flex;
  margin-inline-start: auto;
}

.nav[data-rendering='rail'] .navRow {
  position: relative;
}

.navRailBadge {
  position: absolute;
  inset-block-start: var(--space-half);
  inset-inline-end: var(--space-half);
  display: inline-flex;
}

.navGroupHead {
  display: flex;
  align-items: center;
  flex: none;
  gap: var(--space-2);
  justify-content: space-between;
  width: 100%;
  height: var(--nav-row-height);
  padding: 0 var(--space-3);
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: var(--font-size-caption);
  letter-spacing: 0.08em;
  text-align: left;
  cursor: pointer;
}

.navFooter {
  margin-top: auto;
  padding: var(--space-2);
  border-top: 1px solid var(--border);
}

/* The chevron sits at the right-hand end, away from the labels beneath it. */
.navGroupChevron {
  display: inline-flex;
  margin-left: auto;
}

/* 🔒 Collapse is a row shaped like the entries above it (decision 1441; T692):
   the glyph and the word at the one row height, no pill and no outline. */
.navCollapse {
  display: flex;
  align-items: center;
  flex: none;
  gap: var(--space-2);
  width: 100%;
  height: var(--nav-row-height);
  padding: 0 var(--space-3);
  border: 0;
  background: var(--surface);
  color: var(--text);
  font: inherit;
  font-size: var(--font-size-body);
  text-align: left;
  cursor: pointer;
}

.navCollapse:hover {
  background: var(--surface-2);
}

/* On the rail the Settings row and Collapse centre their glyph, as the entries do. */
.nav[data-rendering='rail'] .navFooter .navRow,
.nav[data-rendering='rail'] .navCollapse {
  justify-content: center;
  padding: 0;
}

.navCollapse:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The pinned control keeps its place while the rows above it scroll. */
.navFooter {
  position: sticky;
  bottom: 0;
  background: var(--surface);
}

/* Below md the desktop bar is replaced outright by the drawer. */
.nav {
  display: none;
}

@media (--bp-md) {
  .nav {
    display: flex;
  }
}

.scrim {
  position: fixed;
  inset: 0;
  z-index: 90;
  border: 0;
  padding: 0;
  background: var(--scrim);
}

/* 🔨 The drawer is as wide as its widest row and no wider (piece 12, decision
   0284). 💰 At a fixed 320px it covered five sixths of a phone to show labels
   a third as wide (product owner, 2026-09-10). The floor keeps an empty drawer
   from collapsing to its close button; the ceiling leaves the page showing. */
.drawer {
  position: fixed;
  inset: var(--header-height) auto 0 0;
  z-index: 95;
  width: fit-content;
  min-width: 160px;
  max-width: 86vw;
  border: 0;
  border-right: 1px solid var(--border);
  padding: 0;
  margin: 0;
  overflow-y: auto;
  background: var(--surface);
}

.drawerBody {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: var(--space-2);
}

/* The screens' own mount inside the frame, keyed on the emulated identity. */
.outlet {
  display: contents;
}

/* 🔒 The passkey offer as a card at the top of the dashboard (decision 1442; T693): it stands above the tiles, once, and never as a strip over the frame. */
.passkeyCard {
  margin-bottom: var(--space-3);
}

/* ---- Page Module ---- */

/* 🔨 One module with three regions, not three cards (piece 12). The banner
   meets the module's top edge, the footer its foot, and the Content region
   between them carries the page's own padding. 💰 They were built as separate
   rounded cards with a gap, so a page read as three floating panels. */
.page {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/* 🔨 Under md the banner takes the height a phone can spare and no more.
   💰 On an iPhone 13 the Calendar's banner alone ran to about 370px of an
   844px screen — a title at the desktop size, a subtitle, the month row and
   the scale row — and the grid under it showed three weeks (product owner,
   2026-09-14). */
.banner {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2);
  background: var(--modal-title-gradient);
  color: var(--on-status-fill);
}

@media (--bp-md) {
  .banner {
    gap: var(--space-3);
    padding: var(--space-3);
    /* 🔒 One height on every page (decision 1440; T685): the banner's avatar and its padding, so a list, a record, the dashboard and Settings open on the same band. */
    min-height: var(--record-banner-height);
  }
}

.bannerText {
  min-width: 0;
}

/*
 * 🔨 The banner's text is two rows and no more (T170): the name and the record's
 * own pills share the first, the kind line takes the second. 💰 It has been three
 * and briefly four, the extra rows a caption and a facts row, and the title bar
 * standard now forbids both.
 */
@media (--bp-md) {
  .bannerText {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .bannerHead {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-1) var(--space-3);
    min-width: 0;
  }

  .bannerHead .bannerKinds {
    margin-top: 0;
  }
}

/* The record type's glyph, left of the title; under md the banner has no room to spare and it goes (product owner, 2026-09-10).
   🔨 Its size is the one token and never the banner's height, so a stack of pills and facts does not grow it (decision 0335). */
.bannerIcon {
  display: none;
  flex: none;
  align-items: center;
  align-self: center;
  width: var(--icon-size-banner);
  height: var(--icon-size-banner);
  color: var(--on-status-fill);
}

/* The small pills under the title: a Company's classifications, a Person's badge (decision 0335). */
.bannerKinds {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  margin-top: var(--space-1);
}

@media (--bp-md) {
  .bannerIcon {
    display: inline-flex;
  }
}

.bannerTitle {
  margin: 0;
  /* The page's own name is the one thing the nav and the glyph do not already
     say, so it stays; it takes the body size under md and the title size up. */
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-title);
  line-height: 1.2;
  /* 🔒 Decision 2152: a name that does not fit on two lines ends in an ellipsis; the whole name stands in the tooltip and the tab title. */
  overflow: hidden;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

@media (--bp-md) {
  .bannerTitle {
    font-size: var(--font-size-title);
  }
}

/* 🔒 Decision 2152: a name that would wrap draws one size step smaller before it is cut (21 steps to 16 above md, 14 to 13 below). */
.bannerTitle[data-title-step='smaller'] {
  font-size: var(--font-size-small);
}

@media (--bp-md) {
  .bannerTitle[data-title-step='smaller'] {
    font-size: var(--font-size-button-large);
  }
}

.bannerSubtitle {
  margin: 0;
  font-size: var(--font-size-subtitle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bannerRight {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* 🔒 The status pills stand on the title's own line and never wrap it (decision 1440; T685): above md the right-hand group keeps its width and the name gives way. */
@media (--bp-md) {
  .bannerRight {
    flex: none;
    flex-wrap: nowrap;
  }
}

/* 🔒 The toolbar row directly beneath the banner (decision 1440; T685): every page-wide action but the banner's one stands here, on the page's white, in the order the page hands them. */
.pageToolbar {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}

/* The summary column a record type's registry entry names (decision 0665): the
   Deal's value and Next Action. It stands between the name and the status pill,
   pushing both of them to the right. Under md the banner has no width for a
   second column, so it wraps onto a line of its own beneath the name. */
.banner[data-banner-column='true'] {
  flex-wrap: wrap;
}

.bannerColumn {
  flex: 1 0 100%;
  order: 1;
  min-width: 0;
}

@media (--bp-md) {
  .banner[data-banner-column='true'] {
    flex-wrap: nowrap;
  }

  .bannerColumn {
    flex: none;
    order: 0;
    margin-left: auto;
  }

  .banner[data-banner-column='true'] .bannerRight {
    margin-left: var(--space-3);
  }
}

/* 🔒 Decision 1472: a record page's title bar folds on a phone whenever it has
   anything beneath its first line, and a folded bar keeps one line, the picture,
   the name and the status pill with the chevron; a long name ends in an ellipsis
   rather than wrapping the bar back to the height it folded away. */
.banner[data-banner-collapsed='true'] .bannerText {
  flex: 1 1 auto;
}

.banner[data-banner-collapsed='true'] .bannerTitle {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The picture slot, standing where the glyph stands and replacing it (decision
   0616; T170). 🔨 Unlike the glyph it survives under the md breakpoint: a picture
   of the person is the one thing on a phone banner that is not text. */
.bannerAvatar {
  display: inline-flex;
  flex: none;
  align-self: center;
}

/* A page's own view controls sit inside the banner and take the width between
   the title and the right-hand slot, spread across it so the bar reads even
   (decision 0329). Under md the banner wraps and the controls take a line of
   their own beneath the title. */
.banner[data-banner-controls='true'] {
  flex-wrap: wrap;
}

.bannerControls {
  flex: 1 1 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2) var(--space-3);
  min-width: 0;
}

@media (--bp-md) {
  .bannerControls {
    flex: 1 1 auto;
  }
}

/* 🔒 Decision 3300 (T2272): a help ring that stands in the controls slot is no view control, so on a phone it takes no row of its own. The banner stays one row, the ring at the right beside the status pill; above md the banner is as it was. */
.banner[data-banner-controls='true'][data-banner-controls-place='inline'] {
  flex-wrap: nowrap;
}

.banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerControls {
  flex: none;
  margin-left: auto;
}

.banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerRight {
  flex: none;
  flex-wrap: nowrap;
  margin-left: 0;
}

/* The pills keep their width and the name gives way, as above md (decision 1440): the name is cut at the end of one line, never the bar grown by a row. */
.banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerTitle {
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

@media (--bp-md) {
  .banner[data-banner-controls='true'][data-banner-controls-place='inline'] {
    flex-wrap: wrap;
  }

  .banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerControls {
    flex: 1 1 auto;
    margin-left: 0;
  }

  .banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerRight {
    margin-left: auto;
  }

  .banner[data-banner-controls='true'][data-banner-controls-place='inline'] .bannerTitle {
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}

/* 🔨 The one scrolling region in the frame, carrying the page's own padding. */
.content {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  /* The gutter is always reserved: a scrollbar that came and went as the canvas relaid itself made the tiles shake (product owner, 2026-09-10). */
  scrollbar-gutter: stable;
  padding: var(--space-3);
  /* The tinted ground the white tiles, cards and lists lift off (decision 0317). */
  background: var(--page-bg);
}

/*
 * 🔒 The footer sits on the floor of the frame with the page short above it
 * (T113), because the content region takes every pixel the banner and this bar
 * leave. 🔨 It clears the home indicator on a phone, which the form's own sticky
 * footer used to do before it moved in here.
 */
.footer {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  padding-bottom: calc(var(--space-3) + var(--safe-bottom));
  border-top: 1px solid var(--border);
  background: var(--surface);
}

.footerDelete {
  margin-right: auto;
}

/* ---- Fallback states ---- */

.fallback {
  display: grid;
  justify-items: center;
  gap: var(--space-3);
  padding: var(--space-6) var(--space-3);
  text-align: center;
  color: var(--text-muted);
}

.fallbackTitle {
  margin: 0;
  font-size: var(--font-size-title);
  color: var(--text);
}

/* ---- Dashboard ---- */

/* 🔨 The page stacks at the page step every other page uses (decision 0601). 💰 The
   dashboard had no wrapper at all and its tiles sat at a raw inline twelve, so the
   landing page and the calendar were the two screens whose spacing did not match the
   rest (product owner, 2026-09-20). */
.dashboardPage {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

/* The large number a tile leads with, and its caption: the whole block is the link into the page behind it. */
.dashboardFigure {
  display: flex;
  flex-direction: column;
  gap: var(--space-half);
  color: inherit;
  text-decoration: none;
}

.dashboardFigureCount {
  color: var(--navy-primary);
  font-size: var(--tile-figure-size);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  font-variant-numeric: var(--figures);
}

.dashboardFigureCaption {
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/* A dashboard tile fills the block the canvas gives it and scrolls its own rows (piece 4). */
.dashboardSlot {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.dashboardSlot > * {
  flex: 1 1 auto;
  min-height: 0;
}

.queueRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) 0;
  color: inherit;
  text-decoration: none;
}

.queueRow + .queueRow {
  border-top: 1px solid var(--surface-2);
}

.queueCount {
  font-variant-numeric: var(--figures);
  font-weight: 700;
}

.queueCount[data-uncounted='true'] {
  color: var(--text-muted);
  font-weight: 400;
}

.feedRow {
  padding: var(--space-2) 0;
}

.feedRow + .feedRow {
  border-top: 1px solid var(--surface-2);
}

.feedWhen {
  display: block;
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

/* The tile library (decision 0264): the bar above the canvas in arrange mode, and the checklist inside the modal. */
.dashboardLibraryBar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: var(--space-2);
}

.dashboardLibrary {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.dashboardEmpty {
  margin: 0 0 var(--space-3);
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/* Session expiry re-authenticates over the current page (piece 21): the
   banner and its compact form sit between the header and the body, so the
   page and its unsaved edits stay where they were. */
.sessionExpired {
  flex: 0 0 auto;
}

.reauth {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-4);
  background: var(--surface-2);
}

/* A link row in the avatar menu draws like its button siblings: the browser's default underline is a link's, and the menu has one link (What's new) beside three buttons. Scoped here so the search panel's rows keep their own look (found in review, 2026-09-22). */
.avatarMenu .searchRow {
  text-decoration: none;
}
